import React, { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { endOfWeek, format, parseISO, startOfWeek } from "date-fns"
import { CalendarIcon, Filter, RefreshCw, Search } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ukToday } from "@/constants/sheriffOptions"
import { useTimesheetStats } from "@/hooks/useTimesheets"
import { cn } from "@/lib/utils"
import { TimesheetFilters as TimesheetFiltersType, TimesheetStatus } from "@/types/timesheet"

import CompactTimesheetList from "../timesheet/CompactTimesheetList"
import TimesheetStats from "../timesheet/TimesheetStats"

// Radix rejects an empty SelectItem value, so "no status filter" needs a value.
// It must not reach the query - status is compared with .eq against the enum column.
const ALL_STATUSES = "all"

const TimesheetsContent: React.FC = () => {
  // Get current week dates
  // The filters are UK days, so the default week is the UK one
  const currentWeekStart = useMemo(() => {
    const today = parseISO(ukToday())
    return format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd")
  }, [])

  const currentWeekEnd = useMemo(() => {
    const today = parseISO(ukToday())
    return format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd")
  }, [])

  const [filters, setFilters] = useState<TimesheetFiltersType>({
    date_from: currentWeekStart,
    date_to: currentWeekEnd,
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [hideApproved, setHideApproved] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const queryClient = useQueryClient()

  // Fetch real stats
  const { data: stats, isLoading: statsLoading } = useTimesheetStats(filters)

  const handleFiltersChange = (newFilters: TimesheetFiltersType) => {
    setFilters(newFilters)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      // Invalidate all timesheet-related queries to trigger refetch
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["timesheets"] }),
        queryClient.invalidateQueries({ queryKey: ["timesheet-stats"] }),
      ])
      toast.success("Data refreshed successfully")
    } catch (error) {
      toast.error("Failed to refresh data")
      console.error("Refresh error:", error)
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Timesheets</h2>
          <p className="text-palette-jungleGreen600">Manage and approve timesheets</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-wrap gap-3 md:gap-6 items-center">
        {/* Status Filter */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
            Status
          </label>
          <Select
            value={filters.status ?? ALL_STATUSES}
            onValueChange={(value: TimesheetStatus | typeof ALL_STATUSES) =>
              handleFiltersChange({
                ...filters,
                status: value === ALL_STATUSES ? undefined : value,
              })
            }
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>All Statuses</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Denied">Denied</SelectItem>
              <SelectItem value="InProgress">In Progress</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date From Filter */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
            From Date
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[200px] justify-start text-left font-normal",
                  !filters.date_from && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.date_from ? format(parseISO(filters.date_from), "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.date_from ? parseISO(filters.date_from) : undefined}
                onSelect={(date) =>
                  handleFiltersChange({
                    ...filters,
                    date_from: date ? format(date, "yyyy-MM-dd") : undefined,
                  })
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Date To Filter */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
            To Date
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[200px] justify-start text-left font-normal",
                  !filters.date_to && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.date_to ? format(parseISO(filters.date_to), "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.date_to ? parseISO(filters.date_to) : undefined}
                onSelect={(date) =>
                  handleFiltersChange({
                    ...filters,
                    date_to: date ? format(date, "yyyy-MM-dd") : undefined,
                  })
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Action Buttons */}
        <div className="ml-auto flex items-end h-full pb-[2px] gap-3">
          <Button
            variant="outline"
            size="default"
            onClick={() =>
              setFilters({
                date_from: currentWeekStart,
                date_to: currentWeekEnd,
              })
            }
          >
            <Filter className="h-4 w-4 mr-2" />
            Reset to Current Week
          </Button>
          <Button variant="outline" size="default" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            {isRefreshing ? "Refreshing..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <TimesheetStats stats={stats} isLoading={statsLoading} />

      {/* Timesheet Content */}
      <div className="bg-white border border-palette-jungleGreen200 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-palette-jungleGreen900">
              Timesheets ({stats?.total_timesheets || 0})
            </h2>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="relative flex items-center gap-2">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-palette-jungleGreen500"
                size={18}
              />
              <Input
                className="pl-10 w-full sm:w-[200px]"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Hide Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHideApproved(!hideApproved)}
              className={hideApproved ? "bg-palette-jungleGreen100" : ""}
            >
              <Filter className="h-4 w-4 mr-1" />
              {hideApproved ? "Show All" : "Hide Approved"}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <CompactTimesheetList
            filters={filters}
            searchTerm={searchTerm}
            hideApproved={hideApproved}
          />
        </div>
      </div>
    </div>
  )
}

export default TimesheetsContent
